// English dictionary (canonical). Add new strings here first, then
// translate them in ./fr.ts and ./de.ts. Missing keys gracefully fall back to
// this English dictionary at runtime.
import type { Dictionary } from "../index";

export const en: Dictionary = {
  // ---- App / brand ----
  "app.name": "HomeBook",
  "app.tagline": "Family-first bookings",

  // ---- Common ----
  "common.signin": "Sign in",
  "common.signup": "Sign up",
  "common.signout": "Sign out",
  "common.cancel": "Cancel",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.edit": "Edit",
  "common.add": "Add",
  "common.loading": "Loading…",
  "common.search": "Search",
  "common.filters": "Filters",
  "common.clearAll": "Clear all",
  "common.none": "None",
  "common.optional": "optional",
  "common.required": "required",
  "common.total": "Total",
  "common.back": "Back",
  "common.publish": "Publish",
  "common.hide": "Hide",
  "common.allTypes": "All types",
  "common.any": "Any",
  "common.noImage": "No image",

  // ---- Header / nav ----
  "nav.browse": "Browse",
  "nav.documentation": "Documentation",
  "nav.myTrips": "My Trips",
  "nav.hostPanel": "Host Panel",
  "nav.admin": "Admin",
  "nav.listProperty": "+ List a property",
  "nav.menu": "Menu",
  "nav.myTripsShort": "My trips",
  "nav.hostPanelShort": "Host panel",
  "nav.adminShort": "Admin console",

  // ---- Roles ----
  "role.ADMIN": "Website Admin",
  "role.PRODUCT_ADMIN": "Property Host",
  "role.USER": "Guest",

  // ---- Reservation status ----
  "status.PENDING": "Pending",
  "status.APPROVED": "Approved",
  "status.REJECTED": "Rejected",
  "status.CANCELLED": "Cancelled",

  // ---- Booking types ----
  "booking.PRIVATE": "Private",
  "booking.SHARED": "Shared",
  "booking.PRIVATE.short": "Whole place",
  "booking.SHARED.short": "Shared stay",
  "booking.PRIVATE.desc": "Book the whole place for yourself. Fixed nightly tariff; the dates lock once your reservation is approved.",
  "booking.SHARED.desc": "Multiple parties can stay at the same time. The location cost is split between all occupying parties, prorata per person per day.",
  "booking.field.partySize": "Party size (number of guests)",
  "booking.field.partySizeHint": "How many people are in your party?",
  "booking.privateTitle": "Private booking",
  "booking.onlySharedHint": "These dates already include a shared stay, so this booking can only be shared.",
  "booking.onlyPrivateHint": "These dates overlap a private stay, so the whole house is needed.",
  "booking.sharedTitle": "Shared booking — cost split prorata",
  "detail.privateBlockHint": "These dates are covered by an approved private (exclusive) booking — please pick other dates.",
  "detail.bookedLegend": "Red = exclusive private stay (locks the whole house) · Violet = shared stay (prorata, number of guests shown).",
  "booking.coOccupants": "Co-occupants on these dates",
  "booking.noCoOccupants": "No other approved parties on these dates yet — you'd pay the full nightly rate if approved alone.",
  "booking.partyN": "{count} {noun}",
  "booking.partyNoun": "guest",
  "partyNounPlural": "guests",
  "booking.occupantsToday": "{count} guests booked",
  "booking.yourShare": "Your projected share",
  "booking.yourShareHint": "Final price is recomputed when approved, based on all approved co-occupants.",
  "booking.perDay": "per day",
  "booking.split": "{share} ({party} of {total} guests)",
  "booking.capacityExceeded": "Those dates are already at capacity.",
  "booking.immutableLocked": "Booking type is locked once reservations exist.",

  // ---- Property types ----
  "property.HOUSE": "House",
  "property.APARTMENT": "Apartment",
  "property.VILLA": "Villa",
  "property.CHALET": "Chalet",
  "property.STUDIO": "Studio",
  "property.COTTAGE": "Cottage",

  // ---- Auth modal ----
  "auth.welcome": "Welcome to HomeBook",
  "auth.subtitle": "Sign in to reserve stays, or create an account to start booking.",
  "auth.tabSignin": "Sign in",
  "auth.tabRegister": "Create account",
  "auth.accountPending": "Your account is awaiting validation. A moderator will validate it shortly — you'll be able to reserve once approved.",
  "auth.demoHint": "Demo: admin / admin123 · guest / guest123",
  "auth.signingIn": "Signing in…",
  "auth.creating": "Creating account…",
  "auth.createAccount": "Create account",

  // Auth fields
  "auth.field.username": "Username",
  "auth.field.usernamePlaceholder": "jane_doe",
  "auth.field.usernameHint": "3-30 chars, letters/numbers/_/-, must start with a letter.",
  "auth.field.firstName": "First name",
  "auth.field.lastName": "Last name",
  "auth.field.email": "Email",
  "auth.field.emailPlaceholder": "you@example.com",
  "auth.field.phone": "Phone (optional)",
  "auth.field.phonePlaceholder": "+1-555-0100",
  "auth.field.password": "Password",
  "auth.field.passwordPlaceholder": "••••••••",
  "auth.field.passwordHint": "At least 6 characters",

  // Username availability
  "username.checking": "Checking availability…",
  "username.available": "Username available",
  "username.taken": "This username is already taken",
  "username.invalid": "Username must be 3-30 chars, start with a letter, and only contain letters, numbers, _ or -.",
  "username.tooShort": "Username is too short",

  // ---- Browse view ----
  "browse.heroBadge": "Family-first bookings",
  "browse.heroTitle": "Book a family house, chalet or apartment",
  "browse.heroSubtitle": "Owners list their properties with photos and availability dates. Guests request reservations. Hosts and the website admin validate every booking — once approved, those dates are locked.",
  "browse.searchPlaceholder": "Search by title, location or description…",
  "browse.resultsCount": "{count} {plural} available",
  "browse.resultSingular": "property",
  "browse.resultPlural": "properties",
  "browse.loadingResults": "Loading…",
  "browse.noResultsTitle": "No properties match your search",
  "browse.noResultsBody": "Try removing some filters or searching different dates.",
  "browse.signInToReserve": "Sign in to reserve a stay.",
  "browse.activeFilters": "Active filters:",
  "browse.guestsChip": "{count}+ guests",

  // Filters
  "filter.propertyType": "Property type",
  "filter.guests": "Guests",
  "filter.checkIn": "Check-in",
  "filter.checkOut": "Check-out",

  // ---- Product card ----
  "card.perNight": "/night",
  "card.bedrooms": "{count} bedroom{plural}",
  "card.bathrooms": "{count} bathroom{plural}",
  "card.guests": "{count} guests max",

  // ---- Product detail ----
  "detail.backToAll": "Back to all properties",
  "layers.title": "Who sees what",
  "layers.axisTop": "more access",
  "layers.axisBottom": "less access",
  "layers.adminScope": "everything, everywhere",
  "layers.ownerScope": "full control of their houses",
  "layers.staffScope": "one assigned house: bookings + bills",
  "layers.userScope": "own reservations + assigned bills",
  "layers.chHouses": "Houses",
  "layers.chBookings": "Bookings",
  "layers.chBills": "Bills",
  "layers.chUsers": "Users",
  "layers.chSettings": "Settings",
  "layers.chStaff": "Staff",
  "layers.chMyTrips": "My Trips",
  "layers.chAssignedBills": "Assigned bills",
  "layers.legendAdmin": "Everything, every house",
  "layers.legendOwner": "Full control of their houses",
  "layers.legendStaff": "One assigned house: bookings + bills, never house editing",
  "layers.legendUser": "Their own trips + bills assigned to them",
  "pd.manage": "Manage this property",
  "pd.changePhoto": "Change this photo",
"pd.addPhoto": "Add a photo",
"pd.photoUpdated": "Photo updated",
  "pd.staff": "House staff",
"pd.staffHint": "Moderators assigned here can validate bookings and manage bills for this house only. They see nothing outside it.",
"pd.addStaff": "Add moderator…",
"pd.staffEmpty": "No moderators assigned yet.",
"pd.staffRemove": "Remove from this house",
"pd.staffAdded": "Moderator added to this house",
"pd.staffRemoved": "Moderator removed",
  "detail.about": "About this property",
  "detail.amenities": "Amenities",
  "detail.availability": "Availability windows",
  "detail.noAvailability": "No availability windows published yet.",
  "detail.alreadyBooked": "Already booked",
  "detail.perNight": "per night",
  "detail.checkIn": "Check-in",
  "detail.checkOut": "Check-out",
  "detail.guestName": "Guest name",
  "detail.guestEmail": "Email",
  "detail.guestPhone": "Phone (optional)",
  "detail.notes": "Notes (optional)",
  "detail.notesPlaceholder": "Number of kids, arrival time, special requests…",
  "detail.priceRow": "{price} × {nights} night{plural}",
  "detail.requestReservation": "Request reservation",
  "detail.signInToReserve": "Sign in to reserve",
  "detail.requesting": "Requesting…",
  "detail.requestHelpText": "Your request is sent to the host for validation. Approved dates become unavailable for everyone else.",

  // ---- My trips ----
  "trips.title": "My trips",
  "trips.subtitle": "Your reservation requests and their status. Hosts validate each request before it's confirmed.",
  "trips.upcoming": "Upcoming",
  "trips.history": "History",
  "trips.signInTitle": "Sign in to view your trips",
  "trips.signInBody": "Your reservation requests and their validation status appear here.",
  "trips.emptyTitle": "No reservations here yet",
  "trips.emptyBody": "Browse properties and request a stay to get started.",
  "trips.cancelRequest": "Cancel request",
  "trips.validatedBy": "Validated by {name}",

  // ---- Host panel ----
  "host.title": "Host panel",
  "host.subtitleAdmin": "Website admin — manage all properties and validate any reservation.",
  "host.subtitleOwner": "Manage your properties and validate reservation requests from guests.",
  "host.tabProperties": "Properties",
  "host.tabReservations": "Reservations",
  "host.pendingCount": "{count} pending",
  "host.listProperty": "List a property",
  "host.noPropertiesTitle": "No properties yet",
  "host.noPropertiesBody": "Create your first property to start receiving reservations.",
  "host.edit": "Edit",
  "host.availability": "Availability",
  "host.photos": "Photos ({count})",
  "host.delete": "Delete",
  "host.published": "Published",
  "host.hidden": "Hidden",
  "host.noAvailabilitySet": "No availability set",
  "host.availWindows": "{count} availability window{plural}",
  "host.awaitingDecision": "Awaiting your decision",
  "host.noPending": "No pending requests right now.",
  "host.allRequests": "All requests",
  "host.noOther": "No other reservations.",
  "host.approve": "Approve",
  "host.decline": "Decline",
  "host.deleteTitle": "Delete this property?",
  "host.deleteBody": "This will remove the property, its photos, availability windows and associated reservations. This action cannot be undone.",

  // ---- Availability modal ----
  "avail.title": "Manage availability",
  "avail.description": "Add the date ranges during which guests can reserve this property. Reservations must fall entirely within one window.",
  "avail.from": "Available from",
  "avail.to": "Available to",
  "avail.addWindow": "Add window",
  "avail.current": "Current windows ({count})",
  "avail.empty": "No availability yet. Add one above so guests can book.",

  // ---- Image manager ----
  "images.title": "Manage photos",
  "images.description": "Upload photos of your property (JPG, PNG, WebP — max 8MB). The first photo is used as the cover image.",
  "images.caption": "Caption (optional)",
  "images.photos": "Photos ({count})",
  "images.empty": "No photos yet.",
  "images.cover": "Cover",

  // ---- Product editor ----
  "productEditor.newTitle": "List a new property",
  "productEditor.editTitle": "Edit property",
  "productEditor.description": "Fill in the details. You can add photos and availability dates after saving.",
  "productEditor.title": "Title",
  "productEditor.titlePlaceholder": "The Martin Family House",
  "productEditor.descriptionLabel": "Description",
  "productEditor.location": "Location",
  "productEditor.locationPlaceholder": "12 Maple Lane, Greenwood Hills",
  "productEditor.type": "Property type",
  "productEditor.bookingType": "Booking type",
  "productEditor.bookingTypeHint": "Private = whole place, fixed tariff, dates lock on approval. Shared = multiple parties, cost split prorata per person per day. Once reservations exist this cannot be changed.",
  "productEditor.price": "Price / night ($)",
  "productEditor.bedrooms": "Bedrooms",
  "productEditor.bathrooms": "Bathrooms",
  "productEditor.maxGuests": "Max guests",
  "productEditor.amenities": "Amenities (comma separated)",
  "productEditor.amenitiesPlaceholder": "WiFi,Parking,Garden,Fireplace",
  "productEditor.saveChanges": "Save changes",
  "productEditor.create": "Create property",
  "productEditor.saving": "Saving…",

  // ---- Admin console ----
  "admin.title": "Admin console",
  "admin.subtitle": "Website administration tools — users, access levels, content & validation overview.",
  "admin.tabOverview": "Overview",
  "admin.tabUsers": "Users",
  "admin.tabReservations": "Reservations",
  "admin.statUsers": "Total users",
  "admin.statProperties": "Properties",
  "admin.statReservations": "Reservations",
  "admin.statRevenue": "Revenue (approved)",
  "admin.resByStatus": "Reservations by status",
  "admin.usersByRole": "Users by access level",
  "admin.addUser": "Add user",
  "admin.colName": "Name",
  "admin.colUsername": "Username",
  "admin.colEmail": "Email",
  "admin.colRole": "Role",
  "admin.colGroup": "Group",
"admin.allRoles": "All roles",
"admin.allGroups": "All groups",
"admin.unassigned": "Unassigned",
  "admin.colProperties": "Properties",
  "admin.colReservations": "Reservations",
  "admin.colStatus": "Status",
  "admin.colActions": "Actions",
  "admin.active": "Active",
  "admin.disabled": "Disabled",
  "admin.deleteUserTitle": "Delete this user?",
  "admin.deleteUserBody": "This permanently removes the account. Their stays stay on the calendars as history, and their bills are left unassigned.",
  "admin.validateAny": "Validate any reservation across the platform.",
  "admin.noPending": "No pending reservations to validate.",
  "admin.recentDecisions": "Recent decisions",

  // ---- User editor ----
  "userEditor.newTitle": "Create user",
  "userEditor.editTitle": "Edit user",
  "userEditor.description": "Manage a user's profile, role and access level.",
  "userEditor.username": "Username",
  "userEditor.firstName": "First name",
  "userEditor.lastName": "Last name",
  "userEditor.email": "Email",
  "userEditor.phone": "Phone (optional)",
  "userEditor.role": "Role / access level",
  "userEditor.active": "Account active",
  "userEditor.password": "Password",
  "userEditor.passwordKeep": "(leave blank to keep current)",
  "userEditor.passwordSet": "Password (blank = auto-generate)",
  "userEditor.passwordPlaceholder": "••••••••",
  "userEditor.saveChanges": "Save changes",
  "userEditor.create": "Create user",
  "userEditor.saving": "Saving…",

  // ---- Language switcher ----
  "lang.label": "Language",
  "lang.changed": "Language updated",

  // ---- Toasts ----
  "toast.welcomeBack": "Welcome back, {name}!",
  "toast.accountCreated": "Account created. Welcome, {name}!",
  "toast.signedOut": "Signed out",
  "toast.reservationRequested": "Reservation requested! The host will validate it shortly.",
  "toast.reservationCancelled": "Reservation cancelled",
  "toast.propertyCreated": "Property created. Add photos & availability next.",
  "toast.propertyUpdated": "Property updated",
  "toast.propertyDeleted": "Property deleted",
  "toast.propertyHidden": "Property hidden",
  "toast.propertyPublished": "Property published",
  "toast.availAdded": "Availability window added",
  "toast.availRemoved": "Removed",
  "toast.photoAdded": "Photo added",
  "toast.photoRemoved": "Photo removed",
  "toast.uploadFailed": "Upload failed",
  "toast.userCreated": "User created",
  "toast.userUpdated": "User updated",
  "toast.userDeleted": "User deleted",
  "toast.userEnabled": "User enabled",
  "toast.userDisabled": "User disabled",
  "toast.reservationApproved": "Reservation approved",
  "toast.reservationRejected": "Reservation rejected",
  "toast.approved": "Approved",
  "toast.rejected": "Rejected",
  "toast.actionFailed": "Action failed",
  "toast.couldNotCancel": "Could not cancel",
  "toast.couldNotUpdate": "Could not update",
  "toast.couldNotDelete": "Could not delete",
  "toast.couldNotSave": "Could not save",
  "toast.couldNotLoad": "Could not load",
  "toast.couldNotLoadUser": "Could not load user",
  "toast.couldNotRemove": "Could not remove",
  "toast.couldNotCreate": "Could not create reservation",
  "toast.pleaseChooseDates": "Please choose check-in and check-out dates",

  // ---- Footer ----
  "footer.tagline": "A booking platform for family houses, chalets and apartments. Owners list, guests reserve, admins validate every stay.",
  "footer.explore": "Explore",
  "footer.allProperties": "All properties",
  "footer.publicPages": "Public house pages",
  "footer.myTrips": "My trips",
  "footer.forHosts": "For hosts",
  "footer.host1": "List a property with photos & dates",
  "footer.host2": "Manage availability windows",
  "footer.host3": "Approve or decline reservation requests",
  "footer.roadmap": "Roadmap",
  "footer.roadmap1": "iOS & Android apps",
  "footer.roadmap2": "WhatsApp group push notifications",
  "footer.roadmap3": "Calendar sync & payments",
  "footer.rights": "All rights reserved.",
  "footer.builtWith": "Built with Next.js, Prisma & salt-hashed passwords.",

  // ---- Misc ----
  "night": "night",
  "nights": "nights",
  "guests": "guests",
  "properties": "properties",

  // ---- Roles (incl. moderator) ----
  "role.MODERATOR": "Moderator",

  // ---- User groups ----
  "group.FAMILY": "Family",
  "group.GUEST": "Guest",
  "group.label": "Group",
  "group.none": "Unassigned",
  "group.familyDesc": "Relatives sharing the family house — benefit from a family discount and can be recorded as expense payers.",
  "group.guestDesc": "External vacation renters — pay the standard seasonal rate.",

  // ---- Validation ----
  "nav.moderator": "Moderation",
  "nav.expenses": "Expenses",
  "nav.dashboard": "Dashboard",
  "nav.notifications": "Notifications",
  "mod.title": "Moderation panel",
  "mod.subtitle": "Everything that waits for a decision: validate new accounts, approve booking requests, and keep the member directory in order.",
  "mod.pending": "Awaiting validation",
  "mod.pendingCount": "{count} awaiting",
  "mod.noPending": "No accounts awaiting validation.",
  "mod.validateAs": "Validate as",
  "mod.validateFamily": "Validate as Family",
  "mod.validateGuest": "Validate as Guest",
  "mod.validated": "Validated",
  "mod.notValidated": "Not validated",
  "mod.signedUp": "Signed up",
  "mod.validatedBy": "Validated by {name}",
  "toast.userValidated": "Account validated",
  "toast.ownershipTransferred": "Ownership transferred",
  "toast.roleUpdated": "Role updated",

  // ---- Notifications ----
  "notif.title": "Notification channels",
  "notif.subtitle": "Where signup / reservation / expense notifications are pushed. Email and WhatsApp providers are pluggable (logs by default; swap for real SMTP / WhatsApp Business API in production).",
  "notif.addEmail": "Add email",
  "notif.addWhatsapp": "Add WhatsApp",
  "notif.target": "Target (email or phone)",
  "notif.label": "Label (optional)",
  "notif.kind": "Kind",
  "notif.EMAIL": "Email",
  "notif.WHATSAPP": "WhatsApp",
  "notif.active": "Active",
  "notif.inactive": "Inactive",
  "notif.history": "Dispatch history",
  "notif.noChannels": "No channels configured. Add an email or WhatsApp target above.",
  "notif.noHistory": "No notifications dispatched yet.",
  "toast.channelAdded": "Channel added",
  "toast.channelRemoved": "Channel removed",
  "toast.channelUpdated": "Channel updated",

  // ---- Season pricing ----
  "season.highSeasonRate": "High-season rate ($/night)",
  "season.highSeasonRateHint": "Applied to days inside a high-season range. Leave 0 to use the base rate everywhere.",
  "season.familyDiscount": "Family discount (%)",
  "season.familyDiscountHint": "Percentage off the nightly rate for FAMILY-group users. 0 = no discount.",
  "season.ranges": "High-season ranges",
  "season.addRange": "Add high-season range",
  "season.rangeName": "Range name",
  "season.from": "From",
  "season.to": "To",
  "season.current": "Current high-season ranges ({count})",
  "season.empty": "No high-season ranges — every day uses the base rate.",
  "season.highSeason": "High season",
  "season.lowSeason": "Low season",
  "season.highSeasonBadge": "High season {rate}",
  "toast.seasonAdded": "High-season range added",
  "toast.seasonRemoved": "High-season range removed",
  "detail.seasonNote": "Seasonal rates apply per day; family discount is applied for FAMILY-group users.",

  // ---- Expenses ----
  "expenses.title": "Expenses & spendings",
  "expenses.subtitle": "Track fixed spendings (taxes, electricity, gas, internet, subscriptions) and punctual spendings (maintenance). Record who paid each bill.",
  "expenses.tabPending": "Pending ({count})",
  "expenses.tabPaid": "Paid ({count})",
  "expenses.tabDueSoon": "Due soon ({count})",
  "expenses.badgeWrittenOff": "Written off",
  "expenses.add": "Add expense",
  "expenses.product": "Property",
  "expenses.type": "Type",
  "expenses.typeFixed": "Fixed (recurring)",
  "expenses.typePunctual": "Punctual (one-off)",
  "expenses.category": "Category",
  "expenses.cat.TAXES": "Taxes (housing & land)",
  "expenses.cat.ELECTRICITY": "Electricity",
  "expenses.cat.GAS": "Gas",
  "expenses.cat.INTERNET": "Internet subscription",
  "expenses.cat.SUBSCRIPTION": "Service subscription",
  "expenses.cat.MAINTENANCE": "Maintenance / repairs",
  "expenses.cat.CUSTOM": "Custom",
  "expenses.customLabel": "Custom label",
  "expenses.description": "Description",
  "expenses.amount": "Amount ($)",
  "expenses.dueDate": "Due date",
  "expenses.periodStart": "Period start",
  "expenses.periodEnd": "Period end",
  "expenses.status": "Status",
  "expenses.PENDING": "Pending",
  "expenses.PAID": "Paid",
  "expenses.markPaid": "Record payment",
  "expenses.paidBy": "Paid by",
  "expenses.paidDate": "Paid on",
  "expenses.paymentMethod": "Payment method",
  "expenses.method.CASH": "Cash",
  "expenses.method.BANK_TRANSFER": "Bank transfer",
  "expenses.method.CARD": "Card",
  "expenses.method.OTHER": "Other",
  "expenses.selectPayer": "Select a family payer",
  "expenses.noFamilyPayers": "No validated FAMILY users yet. Validate a user as Family first.",
  "expenses.noExpenses": "No expenses yet.",
  "expenses.noPaid": "No payments recorded yet.",
  "expenses.totalPending": "Total pending",
  "expenses.totalPaid": "Total paid",
  "toast.expenseAdded": "Expense added",
  "toast.expenseUpdated": "Expense updated",
  "toast.expenseDeleted": "Expense deleted",
  "toast.expensePaid": "Payment recorded",
  "toast.onlyFamilyCanPay": "Only FAMILY-group users can be recorded as payers",

  // ---- Dashboard ----
  "dash.title": "Dashboard",
  "dash.subtitle": "Who paid what and when. Family contributions, expense breakdowns and pending items at a glance.",
  "dash.summaryTotalPaid": "Total paid",
  "dash.summaryTotalPending": "Total pending",
  "dash.summaryPayments": "Payments recorded",
  "dash.summaryPendingUsers": "Users awaiting validation",
  "dash.byPayer": "Paid by family member",
  "dash.byCategory": "Paid by category",
  "dash.recentPayments": "Recent payments (who paid what when)",
  "dash.pendingExpenses": "Pending expenses",
  "dash.noPayments": "No payments recorded yet.",
  "dash.noPendingExpenses": "No pending expenses. Everything is paid!",
  "dash.paidOn": "Paid on",
  "dash.by": "by",
  "dash.noContributions": "No contributions recorded yet.",

  // ---- Admin role-preview (impersonation) ----
  "preview.as": "Preview as user",
  "preview.title": "Preview as user",
  "preview.hint": "Browse the app with another user's role and permissions. Your admin session stays active in the background.",
  "preview.preview": "Preview",
  "preview.exit": "Exit preview",
  "preview.exited": "Preview ended — you are admin again.",
  "preview.started": "Now previewing as {name}",
  "preview.banner": "Previewing as {name} ({role})",

  // ---- User editor: group + validated ----
  "userEditor.group": "User group",
  "userEditor.validated": "Account validated",
  "userEditor.validatedHint": "Validated users can place reservations.",
  "userEditor.generate": "Generate",
"userEditor.createdTitle": "User created",
"userEditor.createdHint": "Copy the username and password now — the password will not be shown again.",
"userEditor.copy": "Copy",
"userEditor.copied": "Copied",
"userEditor.done": "Done",
"userEditor.optional": "(optional)",
"userEditor.maskedNote":
  "Personal data is shown masked for privacy. Retype a field to change it.",

  // ---- Expenses: overview, debts & invoices ----
  "expenses.cardToPay": "To pay",
  "expenses.cardOverdue": "Overdue",
  "expenses.cardDueSoon": "Due soon (30 days)",
  "expenses.tabAll": "All",
  "expenses.tabToPay": "To pay ({count})",
  "expenses.tabDebts": "Debts ({count})",
  "expenses.tabWrittenOff": "Written off ({count})",
  "expenses.badgePending": "To pay",
  "expenses.badgePaid": "Paid",
  "expenses.paidLateBadge": "Paid late",
  "expenses.dayLate": "1 day overdue",
  "expenses.daysLate": "{count} days overdue",
  "expenses.assignedTo": "Assigned to",
  "expenses.assignToHint": "The family member responsible for honoring this bill",
  "expenses.unassigned": "Unassigned",
  "expenses.writeOff": "Write off",
  "expenses.writeOffTitle": "Write off this debt",
  "expenses.writeOffDesc": "This bill stays on record but is no longer counted as owed.",
  "expenses.writeOffNote": "Reason (optional)",
  "expenses.writeOffConfirm": "Write off",
  "expenses.debtsSummary": "Outstanding debts by member",
  "expenses.bills": "{count} bills",
  "expenses.noWrittenOff": "Nothing has been written off.",
  "expenses.noDebts": "No overdue bills — everyone is on track.",
  "expenses.invoice": "Invoice",
  "toast.writeOffDone": "Debt written off",
  "invoice.title": "Invoice",
  "invoice.number": "Invoice no.",
  "invoice.issueDate": "Issue date",
  "invoice.dueDate": "Due date",
  "invoice.from": "From",
  "invoice.billTo": "Bill to",
  "invoice.description": "Description",
  "invoice.category": "Category",
  "invoice.period": "Period",
  "invoice.total": "Total",
  "invoice.status": "Status",
  "invoice.status.PENDING": "Due",
  "invoice.status.OVERDUE": "Overdue",
  "invoice.status.PAID": "Paid",
  "invoice.status.WRITTEN_OFF": "Written off",
  "invoice.paidOn": "Paid on",
  "invoice.notes": "Notes",
  "invoice.print": "Print",
  "invoice.back": "Back to expenses",
  "invoice.notFound": "Invoice not found or access denied.",

  // ---- Families & groups management ----
  "mod.tabValidation": "Validation",
  "mod.tabGroups": "Families & groups",
  "mod.tabOwnership": "Ownership",
  "mod.tabUsers": "Users",
  "mod.tabReview": "To review",
"mod.reviewEmpty": "Nothing is waiting — all clear.",
"mod.queueBookings": "Booking requests",
"mod.queueAccounts": "Accounts",
  "mod.tabBookings": "Booking requests",
"mod.noPendingBookings": "No booking request is waiting.",
"mod.bookingsHint": "Approve or reject the stay requests below. Approving takes the dates; rejecting frees them.",
"permissions.feature.bookings.validate": "Approve / reject booking requests",
"admin.noUsers": "No users match the filters.",
"admin.disable": "Disable account",
"admin.enable": "Enable account",
"invitations.family": "Family (optional)",
"invitations.noFamily": "No family",
"invitations.inviteAnother": "Invite another guest",
"mod.ownershipHint": "Give a house to a member. The new owner gets full control over it and can assign its house staff.",
"mod.currentOwner": "Owner",
"mod.transferSelect": "Select the new owner…",
"mod.transferApply": "Transfer",
"mod.transferConfirm": "Transfer '{house}' to {name}?",
"mod.noHouses": "No houses yet.",
  "mod.chooseFamily": "Which family?",
  "mod.validateInto": "Validate into {group}",
  "groups.subtitle": "Houses belong to families. Members of a family get the family discount on their family's properties.",
  "groups.create": "Create family",
  "groups.name": "Family name",
  "groups.namePlaceholder": "e.g. The Martin Family",
  "groups.members": "{count} member(s)",
  "groups.properties": "{count} property(ies)",
  "groups.addMember": "Add member",
  "groups.removeMember": "Remove from family",
  "groups.removeConfirm": "Remove {name} from the family? They become a guest.",
  "groups.rename": "Rename",
  "groups.delete": "Delete family",
  "groups.deleteConfirm": "Delete the empty family \"{name}\"?",
  "groups.guests": "Guests",
  "groups.guestsHint": "Validated users outside any family.",
  "groups.noFamilies": "No families yet — create one to group members.",
  "groups.candidatesEmpty": "No validated users available.",
  "groups.unassignedFam": "Family members without a family",
  "groups.memberAdded": "{name} joined {family}",
  "groups.memberRemoved": "{name} left the family",
  "groups.familyCreated": "Family created",
  "groups.familyRenamed": "Family renamed",
  "groups.familyDeleted": "Family deleted",

  // ---- Dashboard calendar ----
  "calendar.title": "Availability & bookings",
  "calendar.chooseProperty": "Choose a property",
  "calendar.legendAvailable": "Available",
  "calendar.legendBookedPrivate": "Booked — private",
  "calendar.legendBookedShared": "Booked — shared",
  "calendar.legendPending": "Pending request",
  "calendar.legendMixed": "Booked + pending",
  "calendar.legendOff": "Unavailable / past",
  "calendar.pendingShort": "Pending",
  "calendar.mixedShort": "Booked + pending",
  "calendar.availableShort": "Available",
  "calendar.passedShort": "Passed",
  "calendar.noProperties": "No active properties.",
  "calendar.viewMonth": "Month",
  "calendar.viewYear": "Year",
  "calendar.typeHint": "Booking type of this property: private = exclusive, shared = prorata.",
  "calendar.bookMonth": "Add a booking in this month",
  "calendar.daySuffix": "d",
  "calendar.daySuffixP": "p",
  "calendar.dayDetails": "Bookings on {date}",
  "calendar.exclusiveNote": "This day is exclusively booked with a private stay.",
  "calendar.addHere": "Add booking on this day",
  "calendar.status.APPROVED": "Confirmed",
  "calendar.status.PENDING": "Pending",
  "calendar.typeBlockedHint": "This booking type is not available for the selected dates — an approved booking occupies them. Pick the other type or different dates.",
  "calendar.cancelBooking": "Cancel this booking",
  "calendar.cancelTitle": "Cancel this booking?",
  "calendar.cancelAck": "Confirming will cancel this booking and immediately free its dates for everyone. This action cannot be undone from here.",
  "calendar.cancelConfirm": "Yes, cancel it",
  "calendar.cancelled": "Booking cancelled",


  "calendar.clickHintYear": "Click a day in any month to add a booking, or click a month name.",
  "calendar.addBooking": "Add booking",
  "calendar.bookDay": "New booking on {date}",
  "calendar.bookFor": "For",
  "calendar.myself": "Myself",
  "calendar.approvedHint": "The booking is created as APPROVED: the dates are locked immediately for everyone.",
  "calendar.bookingAdded": "Booking added",
  "calendar.clickHint": "Click a day to add a booking — click an occupied day to see its bookings.",
  "calendar.alreadyBooked": "already booked these dates",

  // ---- Guest invitations ----
  "mod.tabInvitations": "Guest invitations",
  "invitations.inviteGuest": "Invite a guest",
  "invitations.email": "Guest e-mail",
  "invitations.firstName": "First name",
  "invitations.lastName": "Last name",
  "invitations.send": "Create invite & copy link",
  "invitations.sent": "Invitation created — link copied to clipboard",
  "invitations.linkReady": "Share this link with {email}:",
  "invitations.copy": "Copy link",
  "invitations.copied": "Link copied",
  "invitations.sendHint": "The guest opens the link, picks a username and password, and their account is activated instantly.",
  "invitations.history": "Sent invitations",
  "invitations.none": "No invitations sent yet.",
  "invitations.accepted": "Accepted",
  "invitations.pending": "Pending",
  "invitations.expired": "Expired",
  "invitations.by": "by",
  "invitations.revoke": "Revoke invitation",
  "invitations.revoked": "Invitation revoked",
  "invite.title": "You've been invited to HomeBook",
  "invite.hello": "Hello {name}!",
  "invite.username": "Choose a username",
  "invite.usernameRules": "3-30 characters, starts with a letter (letters, digits, _ or -).",
  "invite.password": "Choose a password",
  "invite.confirmPassword": "Confirm password",
  "invite.createAccount": "Create my account",
  "invite.welcome": "Welcome aboard! You are signed in.",
  "invite.invalid": "This invitation is invalid, already used, or expired.",
  "invite.passwordMismatch": "Passwords do not match",

  // ---- Permissions matrix ----
  "admin.tabPermissions": "Permissions",
  "permissions.title": "Role permissions",
  "permissions.hint": "Toggle which features each role can use. Changes apply immediately for every account of that role.",
  "permissions.feature": "Feature",
  "permissions.updated": "Permission updated",
  "permissions.feature.expenses.view": "View the expenses overview",
  "permissions.feature.expenses.manage": "Create / edit / delete expenses",
  "permissions.feature.expenses.payAssigned": "Pay or write off expenses assigned to them",
  "permissions.feature.guests.invite": "Invite guests (create accounts, send links)",
  "permissions.feature.users.validate": "Validate pending user accounts",
  "permissions.feature.families.manage": "Manage families & groups",
  "permissions.feature.products.manage": "Create / edit properties",
  "permissions.feature.calendar.view": "See the booking calendar",
  "permissions.feature.bookings.manage": "Add bookings from the calendar",
  "settings.currencyTitle": "Display currency",
  "settings.currencyHint": "Used everywhere prices are shown. Changing it re-renders all amounts (no conversion of stored values).",
  "settings.currencySaved": "Currency changed to {currency}",
  "expenses.breakdownTitle": "Fixed vs punctual breakdown",
  "expenses.scopeYear": "Yearly",
  "expenses.scopeMonth": "Monthly",
  "expenses.fixedTotal": "Fixed",
  "expenses.punctualTotal": "Punctual",
  "expenses.periodTotal": "Total",
  "expenses.matrixMonth": "Month",
  "expenses.chartTitle": "Spending by month",
"expenses.topCategories": "Where the money goes",
"expenses.dueInDays": "due in {count} days",
"expenses.dueTomorrow": "due tomorrow",
"expenses.dueToday": "due today",
  "expenses.allHouses": "All houses",
  "expenses.changeAssignment": "Change assignment",
"expenses.assignmentSaved": "Assignment saved",
"expenses.unassign": "No assignee (clear)",

  // ---- Notifications ----
  "notif.type.USER_SIGNUP": "New sign-up",
  "notif.type.USER_VALIDATED": "Account validated",
  "notif.type.GUEST_INVITED": "Guest invited",
  "notif.type.RESERVATION_REQUEST": "Reservation request",
  "notif.type.RESERVATION_APPROVED": "Reservation approved",
  "notif.type.RESERVATION_REJECTED": "Reservation rejected",
  "notif.type.BOOKING_ADDED": "Booking added",
  "notif.type.EXPENSE_ASSIGNED": "Expense assigned",
  "notif.type.EXPENSE_PAID": "Expense paid",
  "notif.type.TEST": "Test",
  "notif.channelsTitle": "Channels",
  "notif.editEvents": "Edit events",
  "notif.sendTest": "Send test",
  "notif.testOk": "Test dispatched — check the history and the target inbox/log",
  "notif.testNoProvider": "Test recorded, but no active provider delivered it (console mock only)",
  "notif.typesTitle": "Subscribed events",
  "notif.typesHint": "The channel only receives the selected event types. Leave all unchecked to receive everything.",
  "notif.allTypes": "All events",
  "notif.allTypesFallback": "Nothing selected — the channel will receive every event type.",
  "notif.filterType": "Event type",
  "notif.all": "All",

  // ---- Multi-level navigation + Home screen ----
  "nav.back": "Back",
  "nav.home": "Home",
  "nav.level.browse": "Houses",
  "nav.level.product": "House",
  "nav.level.my-reservations": "My trips",
  "nav.level.host": "Host panel",
  "nav.level.moderator": "Moderation",
  "nav.level.expenses": "Bills",
  "nav.level.dashboard": "Dashboard",
  "nav.level.notifications": "Notifications",
  "nav.level.admin": "Admin console",
  "home.welcome": "Welcome back, {name}",
  "home.welcomeGuest": "Welcome to HomeBook",
  "home.subtitle": "Everything one tap away — pick where you want to go.",
  "home.browse": "Browse houses",
  "home.browseHint": "Photos, prices and the shared calendar",
  "home.myTrips": "My trips",
  "home.myTripsHint": "Your requests, past and upcoming stays",
  "home.host": "Host panel",
  "home.hostHint": "Manage your houses, photos and bookings",
  "home.moderator": "To review",
  "home.moderatorHint": "Bookings to approve and accounts to validate",
  "home.reviewBadge": "pending",
  "home.expenses": "Bills",
  "home.expensesHint": "Who paid what, debts and invoices",
  "home.dashboard": "Dashboard",
  "home.dashboardHint": "Totals by payer, month and category",
  "home.notifications": "Notifications",
  "home.notificationsHint": "Alert channels and dispatch history",
  "home.admin": "Admin console",
  "home.adminHint": "Members, houses, roles and settings",
  "home.docsHint": "How everything works, in plain words",
  "preview.bannerNote": "You are previewing the app as another member.",

  // ---- Occupancy, stay members, dashboard rename ----
  "dash.occupancy": "Occupancy",
  "dash.occupancyHint": "Nights occupied per house and per member",
  "dash.byHouse": "Nights per house",
  "dash.byMember": "Nights per member",
  "dash.totalNights": "Nights booked",
  "dash.noOccupancy": "No approved stay yet.",
  "dash.memberSpend": "Share of stay costs",
  "dash.spendHint": "Each member's part of the stays they joined",
  "stay.members": "Members",
  "stay.membersHint": "Everyone sharing this private stay splits the price equally.",
  "stay.member": "Member",
  "stay.addMember": "Add a member",
  "stay.removeMember": "Remove",
  "stay.organiser": "Organiser",
  "stay.share": "Share",
  "stay.each": "{amount} each",
  "stay.locked": "Approved stay — members are locked.",
  "stay.lockedPerm": "You can still change them (permission: edit approved stays).",
  "stay.sharedSingle": "A shared stay is individual: one member per booking.",
  "stay.memberAdded": "Member added to the stay",
  "stay.memberRemoved": "Member removed from the stay",
  "stay.count": "{count} members",
  "stay.people": "{count} people",
  "permissions.feature.bookings.editAfterApproval": "Add / remove members on an approved private stay",
  "booking.stayMembers": "Who is staying? (private)",
  "booking.stayMembersHint": "Pick the members sharing the house. The price is split equally.",
  "booking.you": "You",

  // ---- Dashboard filter + money charts ----
  "dash.filterHouse": "Filter by house",
  "dash.allHouses": "All houses",
  "dash.expensesPerHouse": "Expenses per house",
  "dash.expensesByCategory": "Expenses by category",
  "dash.paidPerMember": "Paid per member",
  "dash.owedPerMember": "Spend per member (responsible)",
  "dash.totalBilled": "billed",
  "dash.noExpenses": "Nothing billed yet.",
  "dash.chartsHint": "Every figure and chart follows the house filter above.",
};
